import { collection, deleteDoc, doc, getDocs, onSnapshot, query, updateDoc, where } from 'firebase/firestore';
import React, { useEffect, useState } from 'react';
import { FaArrowLeft, FaChevronDown, FaComment, FaGlobe, FaHeart, FaSearch, FaTimes, FaTrash, FaUsers } from 'react-icons/fa';
import { db } from '../../firebase';
import { renderIcon } from '../../utils/iconRenderer';
import ConfirmModal from '../ConfirmModal';

const PAGE_SIZE = 20;

const GroupsModule = ({ selectedCountry }) => {
    const [groups, setGroups] = useState([]);
    const [allPosts, setAllPosts] = useState([]);
    const [users, setUsers] = useState([]); // All users for lookup
    const [masterCategoryItems, setMasterCategoryItems] = useState([]); // Master structural data
    const [masterCategories, setMasterCategories] = useState([]); // Master Categories data
    const [loading, setLoading] = useState(true);
    const [viewingGroup, setViewingGroup] = useState(null);
    const [viewMode, setViewMode] = useState('members'); // 'members' or 'posts'
    const [searchTerm, setSearchTerm] = useState('');
    const [groupMembers, setGroupMembers] = useState([]);
    const [commentsModal, setCommentsModal] = useState({ isOpen: false, post: null });
    const [postComments, setPostComments] = useState([]);
    const [loadingComments, setLoadingComments] = useState(false);
    const [commentSearch, setCommentSearch] = useState('');
    const [likesModal, setLikesModal] = useState({ isOpen: false, post: null });
    const [postLikers, setPostLikers] = useState([]);
    const [loadingLikes, setLoadingLikes] = useState(false);
    const [likerSearch, setLikerSearch] = useState('');
    const [confirmConfig, setConfirmConfig] = useState({ isOpen: false, onConfirm: () => { }, title: '', message: '', type: 'danger' });

    // ISO to Country Name mapping
    const isoToCountryName = React.useMemo(() => ({
        'IN': 'India',
        'BE': 'Belgium',
        'US': 'USA',
        'DE': 'Germany',
        'ES': 'Spain',
        'FR': 'France',
        'NL': 'Netherlands',
        'Global': 'Global'
    }), []);

    // Pagination states
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);


    useEffect(() => {
        // Fetch Master Items for translation fallback lookup
        const unsubItems = onSnapshot(collection(db, 'master_category_items'), (snapshot) => {
            const itemsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setMasterCategoryItems(itemsData);
        });

        // Fetch Master Categories for lookup
        const unsubCats = onSnapshot(collection(db, 'master_categories'), (snapshot) => {
            const catsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setMasterCategories(catsData);
        });

        // Fetch Groups
        const unsubGroups = onSnapshot(collection(db, 'groups'), (snapshot) => {
            const rawGroups = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setGroups(rawGroups);
            setLoading(false);
        });

        // Fetch Posts (we need all to count activity, but we could optimize this later)
        const unsubPosts = onSnapshot(collection(db, 'posts'), (snapshot) => {
            const postsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setAllPosts(postsData);
        });

        // Fetch Users for name lookup
        const unsubUsers = onSnapshot(collection(db, 'users'), (snapshot) => {
            const usersData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                fullName: `${doc.data().firstName || ''} ${doc.data().lastName || ''}`.trim() || 'Anonymous'
            }));
            setUsers(usersData);
        });

        return () => {
            unsubItems();
            unsubCats();
            unsubGroups();
            unsubPosts();
            unsubUsers();
        };
    }, [isoToCountryName]);

    // Process Groups with lookups
    const processedGroups = groups.map(g => {
        const id = g.id;

        // Extract ISO code from ID (e.g., BE_addictions...) or field
        const isoCode = id.split('_')[0].toUpperCase();
        const detectedCountry = isoToCountryName[isoCode] || g.country || 'Global';

        // Check for translations in the group document itself
        const groupTransVal = g.translations?.en || g.translations?.in;
        let resolvedName = (typeof groupTransVal === 'object' ? groupTransVal.name : groupTransVal) || g.name;

        // NEW: Fallback to Master Category Items if name still looks like an ID or is missing (auto-generated ID pattern)
        if (!resolvedName || /^[a-zA-Z0-9]{15,}$/.test(resolvedName)) {
            // Try to find a master item matching varios possible fields
            const strippedId = id.includes('_') ? id.substring(id.indexOf('_') + 1) : id;
            const masterItem = masterCategoryItems.find(mi =>
                mi.id === id ||
                mi.id === strippedId ||
                mi.id === g.itemId ||
                mi.id === g.topicId ||
                mi.id === g.interestId ||
                mi.id === g.subCategoryId ||
                mi.id === g.topic ||
                mi.id === g.interest ||
                mi.id === g.categoryId // This might be used for the item ID in some schema
            );

            if (masterItem) {
                const masterTransVal = masterItem.translations?.en || masterItem.translations?.in;
                const masterName = (typeof masterTransVal === 'object' ? masterTransVal.name : masterTransVal) || masterItem.name;
                if (masterName) resolvedName = masterName;
            }
        }

        // Resolve Category Name from masterCategories
        let resolvedCategory = g.category;
        const categoryDoc = masterCategories.find(c => c.id === g.categoryId || c.id === g.category);
        if (categoryDoc) {
            const catTransVal = categoryDoc.translations?.en || categoryDoc.translations?.in;
            resolvedCategory = (typeof catTransVal === 'object' ? catTransVal.name : catTransVal) || categoryDoc.name || resolvedCategory;
        }

        return {
            ...g,
            name: resolvedName || id,
            category: resolvedCategory || 'General',
            country: detectedCountry,
            isoCode: isoCode
        };
    });

    // Fetch Members for the viewing group from 'group_members' collection
    useEffect(() => {
        if (!viewingGroup) {
            setGroupMembers([]);
            return;
        }

        setLoading(true);
        // Step 1: Query group_members collection
        const qMembers = query(collection(db, 'group_members'), where('groupId', '==', viewingGroup.id));

        const unsubMembers = onSnapshot(qMembers, async (snapshot) => {
            const membersList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Step 2: For each member, we might want their latest user profile info if not in group_members
            // But let's try to shows whatever we have first.
            setGroupMembers(membersList);
            setLoading(false);
        });

        return () => unsubMembers();
    }, [viewingGroup]);

    // Fetch Comments for a specific post
    useEffect(() => {
        if (!commentsModal.isOpen || !commentsModal.post) return;

        setLoadingComments(true);
        const qComments = query(
            collection(db, 'comments'),
            where('postId', '==', commentsModal.post.id)
        );

        const unsubComments = onSnapshot(qComments, (snapshot) => {
            const commentsData = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .sort((a, b) => {
                    const tA = a.createdAt?.seconds || 0;
                    const tB = b.createdAt?.seconds || 0;
                    return tB - tA; // Newest first
                });
            setPostComments(commentsData);
            setLoadingComments(false);
        });

        return () => unsubComments();
    }, [commentsModal]);

    // Fetch Likes — tries multiple Firestore patterns
    useEffect(() => {
        if (!likesModal.isOpen || !likesModal.post) return;

        setLoadingLikes(true);
        setPostLikers([]);

        const post = likesModal.post;
        let unsubscribe = null;

        const resolveName = (userId, fallbackName) => {
            const user = users.find(u => u.id === userId);
            return user?.fullName || fallbackName || 'User';
        };

        const resolvePhoto = (userId) => {
            const user = users.find(u => u.id === userId);
            return user?.photoUrl || null;
        };

        // Pattern 1: likedBy[] array directly in the post document
        if (Array.isArray(post.likedBy) && post.likedBy.length > 0) {
            const likers = post.likedBy.map((uid, idx) => ({
                id: uid || `liker-${idx}`,
                userId: uid,
                displayName: resolveName(uid, null),
                photoUrl: resolvePhoto(uid)
            }));
            setPostLikers(likers);
            setLoadingLikes(false);
            return;
        }

        // Pattern 2: posts/{postId}/likes subcollection
        const subColRef = collection(db, 'posts', post.id, 'likes');
        unsubscribe = onSnapshot(subColRef, (snap) => {
            if (!snap.empty) {
                const likers = snap.docs.map(d => {
                    const data = d.data();
                    const uid = data.userId || data.uid || d.id;
                    return {
                        id: d.id,
                        userId: uid,
                        displayName: resolveName(uid, data.userName || data.displayName),
                        photoUrl: resolvePhoto(uid) || data.photoUrl || null,
                        createdAt: data.createdAt || null
                    };
                });
                setPostLikers(likers);
                setLoadingLikes(false);
                return;
            }

            // Pattern 3: top-level `likes` collection with postId field
            const qLikes = query(collection(db, 'likes'), where('postId', '==', post.id));
            getDocs(qLikes).then(snap2 => {
                const likers = snap2.docs.map(d => {
                    const data = d.data();
                    const uid = data.userId || data.uid || null;
                    return {
                        id: d.id,
                        userId: uid,
                        displayName: resolveName(uid, data.userName || data.displayName),
                        photoUrl: resolvePhoto(uid) || data.photoUrl || null,
                        createdAt: data.createdAt || null
                    };
                });
                setPostLikers(likers);
                setLoadingLikes(false);
            }).catch(() => setLoadingLikes(false));
        });

        return () => { if (unsubscribe) unsubscribe(); };
    }, [likesModal, users]);


    // Filter groups by country and search term
    // Filter and sort groups
    const filteredGroups = processedGroups
        .map(g => ({
            ...g,
            postCount: allPosts.filter(p => p.groupId === g.id || (p.group && p.group.id === g.id)).length
        }))
        .filter(g => {
            const matchesCountry = selectedCountry === 'All' || g.country === selectedCountry;
            const matchesSearch = (g.name || '').toLowerCase().includes(searchTerm.toLowerCase());
            return matchesCountry && matchesSearch;
        })
        .sort((a, b) => b.postCount - a.postCount);

    const paginatedGroups = filteredGroups.slice(0, visibleCount);

    const handleGroupClick = (group, mode = 'members') => {
        setViewingGroup(group);
        setViewMode(mode);
    };

    const handleDeletePost = async (postId) => {
        try {
            await deleteDoc(doc(db, 'posts', postId));
        } catch (error) {
            console.error("Error deleting post:", error);
        }
    };

    const handleDeleteGroup = async (group) => {
        try {
            await deleteDoc(doc(db, 'groups', group.id));
        } catch (error) {
            console.error("Error deleting group:", error);
        }
    };

    const handleToggleStatus = async (group) => {
        try {
            const newStatus = group.status === 'offline' ? 'online' : 'offline';
            await updateDoc(doc(db, 'groups', group.id), {
                status: newStatus
            });
        } catch (error) {
            console.error("Error updating group status:", error);
        }
    };

    const handleDeleteMember = async (memberId) => {
        try {
            await deleteDoc(doc(db, 'group_members', memberId));
        } catch (error) {
            console.error("Error removing member:", error);
        }
    };

    if (loading) {
        return (
            <div className="content-section fade-in">
                <div className="loading-state">
                    <div className="pulse-loader"></div>
                    <p>Loading communities...</p>
                </div>
            </div>
        );
    }

    const groupPosts = viewingGroup
        ? allPosts.filter(p => p.groupId === viewingGroup.id || (p.group && p.group.id === viewingGroup.id))
        : [];

    return (
        <div className="content-section fade-in">

            {/* ─── GROUP LIST VIEW (always mounted, hidden when viewing a group) ─── */}
            <div style={{ display: viewingGroup ? 'none' : 'block' }}>
                <div className="content-header">
                    <div>
                        <h2 className="content-title">Groups Management</h2>
                        <p style={{ margin: 0, color: 'var(--text-tertiary)', fontSize: '14px' }}>
                            Showing {filteredGroups.length} groups {selectedCountry !== 'All' ? `in ${selectedCountry}` : 'globally'}
                        </p>
                    </div>
                    <div className="header-actions">
                        <div className="search-bar-mini">
                            <FaSearch className="search-icon-mini" />
                            <input type="text" placeholder="Search groups..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        </div>
                    </div>
                </div>

                <div className="grid-container">
                    {loading ? (
                        <div className="loading-state" style={{ width: '100%' }}>
                            <div className="pulse-loader"></div>
                            <p>Loading communities...</p>
                        </div>
                    ) : paginatedGroups.map(group => (
                        <div key={group.id} className="group-card" onClick={() => handleGroupClick(group)}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                <div className="group-icon">{renderIcon(group.icon || 'groups', 24)}</div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <h3 className="group-name" style={{ fontSize: '16px', margin: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{group.name}</h3>
                                    <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                                        {group.category} • {group.country}
                                    </div>
                                </div>
                                <div className="group-stats" style={{ flex: 'none', flexShrink: 0 }}>
                                    <div className="group-stat">
                                        <span className="stat-label">Posts</span>
                                        <span className="stat-value">{group.postCount}</span>
                                    </div>
                                </div>
                                <div className="status-toggle-wrap" style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                                    <label className="switch-v2">
                                        <input
                                            type="checkbox"
                                            checked={group.status !== 'offline'}
                                            onChange={() => handleToggleStatus(group)}
                                        />
                                        <span className="slider-v2"></span>
                                    </label>
                                    <span className={`status-label-v2 ${group.status === 'offline' ? 'offline' : 'online'}`}>
                                        {group.status === 'offline' ? 'Offline' : 'Online'}
                                    </span>
                                </div>
                                <button className="btn-action delete" style={{ flexShrink: 0 }} onClick={(e) => {
                                    e.stopPropagation();
                                    setConfirmConfig({
                                        isOpen: true,
                                        onConfirm: () => handleDeleteGroup(group),
                                        title: `Delete group "${group.name}"?`,
                                        message: "This community and all its associated metadata will be removed. Posts will remain but will be unlinked.",
                                        type: 'danger'
                                    });
                                }}><FaTrash /></button>
                            </div>
                        </div>
                    ))}

                    {visibleCount < filteredGroups.length && (
                        <div style={{ textAlign: 'center', marginTop: '20px', width: '100%' }}>
                            <button className="btn-add" onClick={() => setVisibleCount(visibleCount + PAGE_SIZE)}>
                                <FaChevronDown style={{ marginRight: '8px' }} /> Load More Groups
                            </button>
                        </div>
                    )}

                    {!loading && filteredGroups.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '60px', width: '100%', background: 'var(--bg-card)', borderRadius: '20px' }}>
                            <FaSearch size={40} style={{ opacity: 0.1, marginBottom: '16px' }} />
                            <p>No groups found.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* ─── GROUP DETAIL VIEW (only rendered when a group is selected) ─── */}
            {viewingGroup && (
                <div className="fade-in">
                    <div className="content-header">
                        <div className="header-with-back">
                            <button className="back-btn" onClick={() => setViewingGroup(null)}>
                                <FaArrowLeft style={{ marginRight: '8px' }} /> Back to Groups
                            </button>
                            <div className="group-info-header" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <div className="group-icon-header">{renderIcon(viewingGroup.icon || 'groups', 28)}</div>
                                <div>
                                    <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 700 }}>{viewingGroup.name}</h2>
                                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '4px' }}>
                                        <span className="category-tag-v4">{viewingGroup.category || 'General'}</span>
                                        <span style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>
                                            <FaGlobe size={12} /> {viewingGroup.country || 'Global'}
                                        </span>
                                        <div className="status-badge-detail" style={{ marginLeft: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <label className="switch-v2 small">
                                                <input
                                                    type="checkbox"
                                                    checked={viewingGroup.status !== 'offline'}
                                                    onChange={() => handleToggleStatus(viewingGroup)}
                                                />
                                                <span className="slider-v2"></span>
                                            </label>
                                            <span style={{ fontSize: '12px', fontWeight: 600, color: viewingGroup.status === 'offline' ? '#ef4444' : '#22c55e' }}>
                                                {viewingGroup.status === 'offline' ? 'Offline' : 'Online'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="header-actions">
                            <div className="view-mode-tabs">
                                <button className={`view-mode-btn ${viewMode === 'members' ? 'active' : ''}`} onClick={() => setViewMode('members')}>
                                    <FaUsers /> Members
                                </button>
                                <button className={`view-mode-btn ${viewMode === 'posts' ? 'active' : ''}`} onClick={() => setViewMode('posts')}>
                                    <FaComment /> Posts
                                </button>
                            </div>
                        </div>
                    </div>

                    {viewMode === 'members' ? (
                        <div className="table-container" style={{ marginTop: '24px' }}>
                            <table className="data-table">
                                <thead><tr><th>Member</th><th>Role</th><th>Joined At</th><th>Actions</th></tr></thead>
                                <tbody>
                                    {groupMembers.map((member) => {
                                        const userProfile = users.find(u => u.id === member.userId);
                                        const displayName = userProfile?.fullName || member.authorName || member.userName || member.name || 'Member';
                                        return (
                                            <tr key={member.id}>
                                                <td>
                                                    <div className="user-cell">
                                                        <div className="user-avatar">
                                                            {userProfile?.photoUrl ? (
                                                                <img src={userProfile.photoUrl} alt="Avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                                            ) : renderIcon(userProfile?.avatar || member.avatar || 'male', 18)}
                                                        </div>
                                                        <div className="user-info">
                                                            <span className="user-name">{displayName}</span>
                                                            <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>{member.userId}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td><span className={`role-badge ${(member.role || 'Member').toLowerCase()}`}>{member.role || 'Member'}</span></td>
                                                <td>{member.joinedAt ? new Date(member.joinedAt.seconds * 1000).toLocaleDateString() : 'N/A'}</td>
                                                <td><div className="action-buttons"><button className="btn-action delete" title="Remove Member" onClick={() => setConfirmConfig({
                                                    isOpen: true,
                                                    onConfirm: () => handleDeleteMember(member.id),
                                                    title: "Remove Member?",
                                                    message: `Are you sure you want to remove ${displayName} from this group?`,
                                                    type: 'danger'
                                                })}><FaTrash /></button></div></td>
                                            </tr>
                                        );
                                    })}
                                    {groupMembers.length === 0 && (
                                        <tr><td colSpan="4" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>No members found.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="group-posts-view" style={{ marginTop: '24px' }}>
                            <div className="posts-feed-grid-v4">
                                {groupPosts.map(post => (
                                    <div key={post.id} className="post-card">
                                        <div className="post-card-header">
                                            <div className="author-info">
                                                <div className="author-avatar">{renderIcon(post.authorAvatar || 'male', 18)}</div>
                                                <div className="author-details">
                                                    <span className="author-name">{post.authorName || 'User'}</span>
                                                    <span className="author-id">{post.createdAt ? new Date(post.createdAt.seconds * 1000).toLocaleDateString() : 'Recent'}</span>
                                                </div>
                                            </div>
                                            <button className="btn-action delete" onClick={() => setConfirmConfig({
                                                isOpen: true,
                                                onConfirm: () => handleDeletePost(post.id),
                                                title: "Delete Post?",
                                                message: "This post will be permanently removed from the group feed.",
                                                type: 'danger'
                                            })}><FaTrash /></button>
                                        </div>
                                        <div className="post-content" style={{ margin: '12px 0' }}>
                                            {post.title && <h4 style={{ marginBottom: '6px' }}>{post.title}</h4>}
                                            {post.content && <p>{post.content}</p>}
                                            {post.text && <p>{post.text}</p>}

                                            {(() => {
                                                // Robust media detection
                                                const fields = ['video', 'videoUrl', 'v_url', 'mediaUrl', 'imageUrl', 'media', 'image', 'photoUrl', 'attachment', 'postMedia', 'fileUrl'];
                                                let finalUrl = null;
                                                let isVideoPost = post.mediaType === 'video';

                                                for (let field of fields) {
                                                    const val = post[field];
                                                    if (!val) continue;

                                                    if (Array.isArray(val) && val.length > 0) {
                                                        const first = val[0];
                                                        if (typeof first === 'string') {
                                                            finalUrl = first;
                                                        } else if (first && (first.url || first.mediaUrl || first.imageUrl || first.videoUrl)) {
                                                            finalUrl = first.url || first.mediaUrl || first.imageUrl || first.videoUrl;
                                                            if (first.type === 'video' || first.mediaType === 'video') isVideoPost = true;
                                                        }
                                                    } else if (typeof val === 'string') {
                                                        finalUrl = val;
                                                    }
                                                    if (finalUrl) break;
                                                }

                                                if (!finalUrl) return null;

                                                const isV = isVideoPost ||
                                                    (typeof finalUrl === 'string' &&
                                                        (finalUrl.toLowerCase().includes('.mp4') ||
                                                            finalUrl.toLowerCase().includes('.mov') ||
                                                            finalUrl.toLowerCase().includes('.webm') ||
                                                            finalUrl.toLowerCase().includes('.m4v') ||
                                                            finalUrl.toLowerCase().includes('.ogg') ||
                                                            finalUrl.toLowerCase().includes('video')));

                                                return (
                                                    <div className="post-media-container" style={{ marginTop: '12px', width: '100%', display: 'flex', justifyContent: 'center' }}>
                                                        {isV ? (
                                                            <video
                                                                src={finalUrl}
                                                                controls
                                                                playsInline
                                                                preload="metadata"
                                                                style={{ width: '100%', maxHeight: '400px', borderRadius: '12px', background: '#000' }}
                                                            />
                                                        ) : (
                                                            <img
                                                                src={finalUrl}
                                                                alt="Post media"
                                                                style={{ width: '100%', maxHeight: '400px', borderRadius: '12px', objectFit: 'cover', cursor: 'pointer' }}
                                                                onClick={() => window.open(finalUrl, '_blank')}
                                                            />
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                        <div style={{ display: 'flex', gap: '16px', paddingTop: '12px', borderTop: '1px solid var(--border-light)' }}>
                                            <button className="btn-text-action" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setLikesModal({ isOpen: true, post }); }}>
                                                <FaHeart color="#ef4444" /> {post.likes || 0}
                                            </button>
                                            <button className="btn-text-action" onClick={(e) => { e.stopPropagation(); setCommentsModal({ isOpen: true, post }); }}>
                                                <FaComment color="#2B7FD9" /> {post.commentCount || 0}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                {groupPosts.length === 0 && (
                                    <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-tertiary)' }}>
                                        <FaComment size={40} style={{ opacity: 0.1, marginBottom: '16px' }} />
                                        <p>No posts in this group yet.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Comments Modal */}
                    {commentsModal.isOpen && (() => {
                        const filteredComments = postComments.filter(comment => {
                            const cu = users.find(u => u.id === comment.userId || u.id === comment.authorId);
                            const name = cu?.fullName || comment.authorName || comment.userName || '';
                            const text = comment.content || comment.text || '';
                            return name.toLowerCase().includes(commentSearch.toLowerCase()) || text.toLowerCase().includes(commentSearch.toLowerCase());
                        });
                        return (
                            <div className="modal-overlay" onClick={() => { setCommentsModal({ isOpen: false, post: null }); setCommentSearch(''); }}>
                                <div className="modal-content" style={{ maxWidth: '560px', width: '90%' }} onClick={e => e.stopPropagation()}>
                                    <div className="modal-header" style={{ borderBottom: '1px solid var(--border-light)', paddingBottom: '16px' }}>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>💬 Comments</h3>
                                            <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                                                {postComments.length} total{filteredComments.length !== postComments.length ? ` · ${filteredComments.length} shown` : ''}
                                            </p>
                                        </div>
                                        <button className="close-btn" onClick={() => { setCommentsModal({ isOpen: false, post: null }); setCommentSearch(''); }}><FaTimes /></button>
                                    </div>
                                    <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border-light)' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'var(--bg-secondary)', borderRadius: '10px', padding: '8px 14px' }}>
                                            <FaSearch style={{ color: 'var(--text-tertiary)', fontSize: '13px' }} />
                                            <input type="text" placeholder="Search by name or comment..." value={commentSearch} onChange={e => setCommentSearch(e.target.value)} autoFocus
                                                style={{ border: 'none', background: 'transparent', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', width: '100%' }} />
                                            {commentSearch && <button onClick={() => setCommentSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 0 }}><FaTimes size={12} /></button>}
                                        </div>
                                    </div>
                                    <div className="modal-body custom-scrollbar" style={{ maxHeight: '55vh', overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {loadingComments ? <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>Loading...</div>
                                            : filteredComments.length === 0 ? (
                                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                                    <FaComment size={36} style={{ opacity: 0.1, marginBottom: '12px' }} />
                                                    <p style={{ color: 'var(--text-tertiary)', margin: 0 }}>{commentSearch ? `No results for "${commentSearch}"` : 'No comments yet.'}</p>
                                                </div>
                                            ) : filteredComments.map(comment => {
                                                const cu = users.find(u => u.id === comment.userId || u.id === comment.authorId);
                                                const name = cu?.fullName || comment.authorName || comment.userName || 'Anonymous';
                                                const date = comment.createdAt ? new Date(comment.createdAt.seconds * 1000).toLocaleString() : '';
                                                return (
                                                    <div key={comment.id} style={{ display: 'flex', gap: '12px', padding: '14px', background: 'var(--bg-secondary)', borderRadius: '14px', alignItems: 'flex-start' }}>
                                                        <div className="user-avatar" style={{ width: '38px', height: '38px', flexShrink: 0 }}>
                                                            {cu?.photoUrl ? <img src={cu.photoUrl} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : renderIcon(cu?.avatar || 'male', 18)}
                                                        </div>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                                                                <div>
                                                                    <span style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)' }}>{name}</span>
                                                                    {date && <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginLeft: '8px' }}>{date}</span>}
                                                                </div>
                                                                <button onClick={() => setConfirmConfig({
                                                                    isOpen: true,
                                                                    onConfirm: async () => await deleteDoc(doc(db, 'comments', comment.id)),
                                                                    title: "Delete Comment?",
                                                                    message: "Remove this comment from the post permanently?",
                                                                    type: 'danger'
                                                                })}
                                                                    style={{ background: 'rgba(239,68,68,0.1)', border: 'none', borderRadius: '6px', color: '#ef4444', cursor: 'pointer', padding: '4px 8px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                                                                    <FaTrash size={10} /> Delete
                                                                </button>
                                                            </div>
                                                            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>{comment.content || comment.text}</p>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Likes Modal */}
                    {likesModal.isOpen && (() => {
                        const filteredLikers = postLikers.filter(l => l.displayName?.toLowerCase().includes(likerSearch.toLowerCase()));
                        return (
                            <div className="modal-overlay" onClick={() => { setLikesModal({ isOpen: false, post: null }); setLikerSearch(''); }}>
                                <div className="modal-content" style={{ maxWidth: '420px', width: '90%' }} onClick={e => e.stopPropagation()}>
                                    <div className="modal-header" style={{ borderBottom: '1px solid var(--border-light)', paddingBottom: '16px' }}>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>❤️ Likes</h3>
                                            <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>{postLikers.length} total{filteredLikers.length !== postLikers.length ? ` · ${filteredLikers.length} shown` : ''}</p>
                                        </div>
                                        <button className="close-btn" onClick={() => { setLikesModal({ isOpen: false, post: null }); setLikerSearch(''); }}><FaTimes /></button>
                                    </div>
                                    <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-light)' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'var(--bg-secondary)', borderRadius: '10px', padding: '8px 14px' }}>
                                            <FaSearch style={{ color: 'var(--text-tertiary)', fontSize: '13px' }} />
                                            <input type="text" placeholder="Search by name..." value={likerSearch} onChange={e => setLikerSearch(e.target.value)} autoFocus
                                                style={{ border: 'none', background: 'transparent', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', width: '100%' }} />
                                            {likerSearch && <button onClick={() => setLikerSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 0 }}><FaTimes size={12} /></button>}
                                        </div>
                                    </div>
                                    <div className="modal-body custom-scrollbar" style={{ maxHeight: '55vh', overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {loadingLikes ? <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>Loading...</div>
                                            : filteredLikers.length === 0 ? (
                                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                                    <FaHeart size={36} style={{ opacity: 0.1, marginBottom: '12px', color: '#ef4444' }} />
                                                    <p style={{ color: 'var(--text-tertiary)', margin: 0 }}>{likerSearch ? `No results for "${likerSearch}"` : 'No likes yet.'}</p>
                                                </div>
                                            ) : filteredLikers.map((liker, idx) => {
                                                const lu = users.find(u => u.id === liker.userId);
                                                return (
                                                    <div key={liker.id || idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
                                                        <div className="user-avatar" style={{ width: '36px', height: '36px', flexShrink: 0 }}>
                                                            {lu?.photoUrl ? <img src={lu.photoUrl} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : renderIcon(lu?.avatar || 'male', 18)}
                                                        </div>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)' }}>{liker.displayName}</div>
                                                            {liker.createdAt && <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{new Date(liker.createdAt.seconds * 1000).toLocaleDateString()}</div>}
                                                        </div>
                                                        <FaHeart color="#ef4444" size={12} />
                                                    </div>
                                                );
                                            })}
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </div>
            )}

            <ConfirmModal
                isOpen={confirmConfig.isOpen}
                onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                type={confirmConfig.type}
            />
        </div>
    );
};

export default GroupsModule;

